import {
  FinanceRate,
  InfoWorkType,
  WorkType,
} from '@kanda-libs/ks-frontend-services';
import { pipe } from 'fp-ts/lib/function';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import {
  filterOut149If129,
  filterOutPropensio149IfAllium149,
  getRatesFromInfoWorkTypesByWorkType,
} from 'utils';

export type FinanceFormType = 'monthlyPayments' | 'borrowAmount';

export interface FinanceCalculatorFormValues {
  type: FinanceFormType;
  amountToBorrow?: number;
  amountToPayMonthly?: number;
  apr?: string;
}

export interface FinanceCalculatorContextType {
  infoWorkTypes: InfoWorkType[] | undefined;
  infoWorkType: InfoWorkType | undefined;
  workType: WorkType | undefined;
  rates: FinanceRate[] | undefined;
  stored: FinanceCalculatorFormValues;
  initialValue: number;
  setStored: Dispatch<SetStateAction<FinanceCalculatorFormValues>>;
  onMonthlyPaymentsClick: () => void;
  onBorrowAmountClick: () => void;
  onSelectAPR: (apr: string) => void;
  isLoading: boolean;
}

export const FinanceCalculatorContext =
  createContext<FinanceCalculatorContextType>({
    infoWorkTypes: undefined,
    infoWorkType: undefined,
    workType: undefined,
    rates: undefined,
    initialValue: 0,
    stored: {
      type: 'monthlyPayments',
    },
    setStored: () => {},
    onMonthlyPaymentsClick: () => {},
    onBorrowAmountClick: () => {},
    onSelectAPR: () => {},
    isLoading: true,
  });

export const useFinanceCalculatorContext = () =>
  useContext(FinanceCalculatorContext);

export interface FinanceCalculatorProviderProps {
  children: ReactNode;
  infoWorkTypes: InfoWorkType[] | undefined;
  workType: WorkType | undefined;
  initialValue?: number;
  isLoading?: boolean;
}

const FinanceCalculatorProvider: FunctionComponent<FinanceCalculatorProviderProps> =
  function ({
    children,
    infoWorkTypes,
    workType,
    isLoading = false,
    initialValue = 0,
  }) {
    const [stored, setStored] = useState<FinanceCalculatorFormValues>({
      type: 'monthlyPayments',
    });

    const onSelectAPR = useCallback(
      (apr: string) => {
        setStored({
          ...stored,
          apr,
        });
      },
      [stored],
    );

    const onMonthlyPaymentsClick = useCallback(() => {
      if (stored.type === 'monthlyPayments') return;
      setStored({
        type: 'monthlyPayments',
        amountToPayMonthly: undefined,
        amountToBorrow: undefined,
        apr: stored.apr,
      });
    }, [stored]);

    const onBorrowAmountClick = useCallback(() => {
      if (stored.type === 'borrowAmount') return;
      setStored({
        type: 'borrowAmount',
        amountToPayMonthly: undefined,
        amountToBorrow: undefined,
        apr: stored.apr,
      });
    }, [stored]);

    const infoWorkType = useMemo(() => {
      if (
        isLoading ||
        !infoWorkTypes ||
        infoWorkTypes.length === 0 ||
        !workType
      )
        return undefined;
      return getRatesFromInfoWorkTypesByWorkType(infoWorkTypes, workType);
    }, [isLoading, infoWorkTypes, workType]);

    const rates = useMemo(() => {
      if (!infoWorkType) return undefined;
      const infoRates = infoWorkType.rates;
      return pipe(
        infoRates,
        filterOut149If129,
        filterOutPropensio149IfAllium149,
        // filterOutHummIfOtherRegulated,
      );
    }, [infoWorkType]);

    const values = useMemo(
      () => ({
        infoWorkTypes,
        infoWorkType,
        workType,
        rates,
        stored,
        initialValue,
        setStored,
        onMonthlyPaymentsClick,
        onBorrowAmountClick,
        onSelectAPR,
        isLoading,
        // isLoading: !isLoading,
      }),
      [
        stored,
        infoWorkTypes,
        infoWorkType,
        workType,
        rates,
        initialValue,
        onMonthlyPaymentsClick,
        onBorrowAmountClick,
        onSelectAPR,
        isLoading,
      ],
    );

    return (
      <FinanceCalculatorContext.Provider value={values}>
        {children}
      </FinanceCalculatorContext.Provider>
    );
  };

export default FinanceCalculatorProvider;
